import React, { useCallback, useEffect, useRef, useState } from 'react';
import useSize from '@react-hook/size';
import debounce from 'lodash/debounce';
import maxBy from 'lodash/maxBy';
import { DraggableCategory } from './DraggableCategory';
import { DraggableProductListHeader } from './DraggableProductListHeader';
import { LayoutFullHeightContainer } from '../Layout/LayoutFullHeightContainer';
import { makeStyles } from '@material-ui/core/styles';
import { MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { ProductListProps } from 'components/ProductList/ProductList';
import classNames from 'classnames';

interface MakeStyleProps {
    containerWidth: number;
}

const useStyles = makeStyles(theme => ({
    stickyHeader: {
        position: 'sticky',
        height: 48,
        width: (props: MakeStyleProps) => props.containerWidth,
        backgroundColor: '#fafafa',
        top: 80,
        left: 0,
        zIndex: 1,
        // overflowY: 'hidden',
    },
    list: {
        height: '100%',
        padding: '0 40px',
        [theme.breakpoints.down(640)]: {
            padding: '0 16px',
        },
    },
    listItem: {
        flex: '0 0 50%',
        minWidth: 0,
        [theme.breakpoints.down(640)]: {
            flex: '0 0 100%',
        },
        [theme.breakpoints.up(1920)]: {
            flex: '0 0 33%',
        },
    },
    gridContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
    },
    editModeContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingTop: theme.spacing(1),
    },
    editModeLabel: {
        paddingRight: theme.spacing(1),
    },
    pinToTop: {
        marginTop: 80,
    },
}));

export const DraggableProductList = (props: ProductListProps) => {
    const [activeTab, setActiveTab] = useState(0);
    const anchorRefs = useRef<Record<string, HTMLDivElement>>({});

    const nonEmptyCategories = props.categories.filter(c => {
        const categoryProducts = props.products[c.categoryId];
        return categoryProducts && categoryProducts.length > 0;
    });

    const activeTabChangedManually = useRef(false);

    const setActiveTabManually = useCallback(
        newActiveTabIndex => {
            activeTabChangedManually.current = true;
            setActiveTab(newActiveTabIndex);
        },
        [activeTabChangedManually, setActiveTab],
    );

    const handleTabClick = useCallback(
        (categoryId: string) => {
            const ref = anchorRefs.current[categoryId];
            if (ref) {
                ref.scrollIntoView({
                    block: 'center',
                });
            }
        },
        [anchorRefs.current],
    );

    useEffect(() => {
        const onScroll = debounce(() => {
            if (!activeTabChangedManually.current) {
                const visibleAreas = Object.values(anchorRefs.current).map((div, index) => {
                    if (!div) {
                        return { area: -1, index };
                    }
                    const rect = div.getBoundingClientRect();
                    const a = Math.max(0, rect.top);
                    const b = Math.min(window.outerHeight, rect.bottom);
                    return { area: b - a, index };
                });
                const { index } = maxBy(visibleAreas, 'area');
                setActiveTab(index);
            }
            activeTabChangedManually.current = false;
        }, 150);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, [nonEmptyCategories, activeTabChangedManually]);

    const container = useRef<HTMLDivElement>();
    const [containerWidth] = useSize(container);

    const classes = useStyles({ containerWidth });

    const mouseSensor = useSensor(MouseSensor, {
        activationConstraint: {
            distance: 10,
        },
    });
    const touchSensor = useSensor(TouchSensor, {
        activationConstraint: {
            delay: 150,
            tolerance: 5,
        },
    });

    const sensors = useSensors(mouseSensor, touchSensor);

    return (
        <div ref={container} className={props.pinToTopCategories && classes.pinToTop}>
            <LayoutFullHeightContainer>
                <div className={classes.stickyHeader}>
                    <DraggableProductListHeader
                        activeTab={activeTab}
                        setActiveTab={setActiveTabManually}
                        categories={nonEmptyCategories}
                        sensors={sensors}
                        onTabClick={handleTabClick}
                    />
                </div>
                <div className={classes.list} itemType="http://schema.org/BreadcrumbList">
                    {nonEmptyCategories.map(category => (
                        <DraggableCategory
                            category={category}
                            categoryProducts={props.products[category.categoryId]}
                            storeCategory={props.storeCategory}
                            key={category.categoryId}
                            sensors={sensors}
                            anchorRef={anchorRefs}
                            onProductClick={props.onProductClick}
                        />
                    ))}
                </div>
            </LayoutFullHeightContainer>
        </div>
    );
};
